import { Row, Col, Form, Button, FormGroup, Placeholder } from 'react-bootstrap'
import { useForm, useFieldArray, UseFormRegister, SubmitHandler, Path } from 'react-hook-form'
import Project from '@/types/Project'
import React, { useEffect } from 'react'
import LoadingButton from '@/components/shared/LoadingButton'
import { useNavigate, useParams } from 'react-router'
import FileInput, { FileFormat } from '@/components/FileInput'
import '@/utils/formData'
import { useToaster } from '@/components/shared/ToasterProvider'
import Interview from '@/types/Interview'
import { useGetProjectQuery, useCreateProjectMutation, useUpdateProjectMutation } from '@/features/apiSliceWithProjects'
import useToastQuery from '@/hooks/useToastQuery'
import useToastMutation from '@/hooks/useToastMutation'

export type FormProject =
  Omit<Project, 'id' | 'viewCount' | 'summary' | 'recording' | 'photos' | 'attachments'>
  & {
  summary: string,
  recording?: FileList,
  photos?: FileList,
  attachments?: FileList,
  interviews: Partial<Interview>[],
}

// because file data are present in the form, FormData has to be used
function buildRequestBody(data: FormProject): FormData {
  const formData = new FormData()
  formData.append('project[title]', data.title)
  formData.append('project[vector_store_id]', data.vectorStoreId ?? '')
  data.summary
    .split('\n')
    .filter(line => line?.trim().length > 0)
    .map(line => line.replace(/^\d+\.\s*/, '').trim())
    .forEach(line => formData.append('project[summary][]', line))

  // only add file data that are uploaded explicitly via form
  // existing files that are not changed won't be added to the form data, so they will be preserved in the
  // backend existing files are removed explicitly via a separate delete button that will send a request to
  // delete the file right away without waiting for the form submit
  formData.appendFileList<Project>(data.recording, 'project', 'recording')
  formData.appendFileList<Project>(data.photos, 'project', 'photos')
  formData.appendFileList<Project>(data.attachments, 'project', 'attachments')

  data.interviews.forEach((interview, idx) => {
    if (interview?.id) {
      formData.append(`project[interviews_attributes][${idx}][id]`, interview?.id.toString())
    }
    formData.append(`project[interviews_attributes][${idx}][title]`, interview.title)
    formData.append(`project[interviews_attributes][${idx}][transcript]`, interview.transcript)
    if (interview?._destroy === true) {
      formData.append(`project[interviews_attributes][${idx}][_destroy]`, 'true')
    }
  })

  return formData
}

export default function ProjectForm() {
  const navigate = useNavigate()
  const { projectId: projectIdParam } = useParams()
  const projectId = projectIdParam ? Number(projectIdParam) : undefined
  const {
    isFetching,
    data: draftProject,
  } = useToastQuery(useGetProjectQuery, projectId || -1, { skip: !projectId })
  const [createProject, { isLoading: isCreatingProject }] = useToastMutation(useCreateProjectMutation)
  const [updateProject, { isLoading: isUpdatingProject }] = useToastMutation(useUpdateProjectMutation)
  const showToaster = useToaster()
  const { control, register, handleSubmit, reset } = useForm<FormProject>()

  const action = projectId ? 'update' : 'create'
  const title = projectId ? 'Update project' : 'Create project'

  useEffect(() => {
    if (!draftProject) return

    reset({
      ...draftProject,
      vectorStoreId: draftProject.vectorStoreId,
      summary: Array.isArray(draftProject.summary) ? draftProject.summary.join('\n') : '',
      photos: undefined,
      recording: undefined,
      attachments: undefined,
    })
  }, [draftProject])

  // Bump this value to force FileInput remounts and clear the native file input state after reset/load.
  // ex. after updating the form with some files so we force the remount of the input to get clear
  // since just resetting the React hook state doesn't affect the DOM input elements
  // Note: Attachment deletions are now handled by the Redux thunk in DeleteRecordButton,
  // so we don't need onAttachmentDeleted callback anymore

  const onSubmit: SubmitHandler<FormProject> = async (formProject) => {
    const formDataProject = buildRequestBody(formProject)
    const { data: project } = await (projectId
                                     ? updateProject({ projectId, body: formDataProject })
                                     : createProject(formDataProject))

    navigate(`/projects/${project!.id}/`)
    showToaster({ message: `Project ${formProject.title} ${action}d successfully!` })
  }

  const { fields, append, update, remove } = useFieldArray({
    control,
    name: 'interviews',
    keyName: 'fieldId',
  })

  if (isFetching) return <ProjectFormSkeleton />

  return (
    <Row className="justify-content-center mt-4">
      <Col lg={8} md={10} sm={12}>
        <h3 className="mb-4">{title}</h3>
        <Form
          className="d-flex flex-column gap-3"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Form.Group>
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter project title"
              {...register('title', { required: true })}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Vector store ID</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter project vector store ID"
              {...register('vectorStoreId', { required: true })}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Summary</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              placeholder="Enter project summary"
              {...register('summary')}
            />
          </Form.Group>
          <FileInput
            title="Audio recording"
            accept={[FileFormat.audio, FileFormat.video]}
            attachments={draftProject?.recording ? [draftProject.recording] : []}
            {...register('recording')}
          />
          <FileInput
            title="Photos"
            accept={[FileFormat.image]}
            attachments={draftProject?.photos}
            multiple
            {...register('photos')}
          />
          <FileInput
            title="Attachments"
            accept={[FileFormat.any]}
            attachments={draftProject?.attachments}
            multiple
            {...register('attachments')}
          />
          <Button
            variant="outline-primary"
            type="button"
            onClick={() => append({ title: '', transcript: '' })}
          >
            Add transcript text
          </Button>
          {fields.map((field, index) => field._destroy === true ? null : (
            <React.Fragment key={field.fieldId}>
              <hr className="my-4 border-secondary border-1" />
              <InterviewForm
                register={register}
                index={index}
                onRemove={index => {
                  const interview = fields[index]
                  // if an interview has _destroy prop, then it's a record from backend
                  // if an interview doesn't have `_destroy` it's a draft record created
                  // in the frontend so just remove it from an array
                  interview._destroy === false
                  ? update(index, { ...interview, _destroy: true })
                  : remove(index)
                }}
              />
            </React.Fragment>
          ))}
          <hr className="my-4 border-secondary border-1" />
          <LoadingButton
            type="submit"
            loading={isCreatingProject || isUpdatingProject}
            text={title}
            className="mb-5"
          />
        </Form>
      </Col>
    </Row>
  )
}

interface InterviewFormProps {
  register: UseFormRegister<FormProject>
  index: number
  onRemove: (index: number) => void
}

function InterviewForm({
  index,
  register,
  onRemove,
}: InterviewFormProps) {
  return (
    <>
      <h4>{`Interview ${index + 1}`}</h4>
      <FormGroup>
        <Form.Label>Title</Form.Label>
        <Form.Control
          {...register(`interviews.${index}.title` as Path<FormProject>, { required: true })}
        />
      </FormGroup>
      <FormGroup>
        <Form.Label>Transcript</Form.Label>
        <Form.Control
          as="textarea"
          rows={5}
          {...register(`interviews.${index}.transcript` as Path<FormProject>, { required: true })}
        />
      </FormGroup>
      <Button
        variant="danger"
        className="w-100 mt-3"
        onClick={() => onRemove(index)}
      >
        Remove transcript text
      </Button>
    </>
  )
}

function ProjectFormSkeleton() {
  return (
    <Row className="justify-content-center mt-4">
      <Col lg={8} md={10} sm={12} className="d-flex flex-column gap-3">
        <Placeholder as="h3" animation="glow"><Placeholder xs={4} /></Placeholder>
        <div>
          <Placeholder as="p" animation="glow" className="mb-1"><Placeholder xs={2} /></Placeholder>
          <Placeholder animation="glow"><Placeholder xs={12}
                                                     style={{ height: 38, borderRadius: 4 }} /></Placeholder>
        </div>
        <div>
          <Placeholder as="p" animation="glow" className="mb-1"><Placeholder xs={3} /></Placeholder>
          <Placeholder animation="glow"><Placeholder xs={12}
                                                     style={{ height: 38, borderRadius: 4 }} /></Placeholder>
        </div>
        <div>
          <Placeholder as="p" animation="glow" className="mb-1"><Placeholder xs={2} /></Placeholder>
          <Placeholder animation="glow"><Placeholder xs={12}
                                                     style={{ height: 120, borderRadius: 4 }} /></Placeholder>
        </div>
        <Placeholder animation="glow"><Placeholder xs={12}
                                                   style={{ height: 38, borderRadius: 4 }} /></Placeholder>
        <Placeholder animation="glow"><Placeholder xs={12}
                                                   style={{ height: 38, borderRadius: 4 }} /></Placeholder>
        <Placeholder animation="glow"><Placeholder xs={12}
                                                   style={{ height: 38, borderRadius: 4 }} /></Placeholder>
        <hr className="my-4 border-secondary border-1" />
      </Col>
    </Row>
  )
}
